import React, { useState, useEffect } from "react";
import "../styles/Login.css";
import { useNavigate } from "react-router-dom";
import { buildApiUrl, API_ENDPOINTS, APP_DISPLAY_NAME } from "../config/api";
import { apiRequest } from "../utils/apiUtils";
import PasswordStrengthChecklist from "../components/common/PasswordStrengthChecklist.jsx";

const PASSWORD_RULES = [
  (p) => p.length >= 8,
  (p) => /[A-Z]/.test(p),
  (p) => /[a-z]/.test(p),
  (p) => /[0-9]/.test(p),
  (p) => /[^A-Za-z0-9]/.test(p),
];

const allRulesPass = (p) => PASSWORD_RULES.every((rule) => rule(p));

const SetPassword = () => {
  const [token, setToken] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [passwords, setPasswords] = useState({ password: "", confirmPassword: "" });
  const [showPasswords, setShowPasswords] = useState({ password: false, confirmPassword: false });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (t) {
      setToken(t);
      window.history.replaceState(null, "", window.location.pathname);
    }
    setInitialized(true);
  }, []);

  const passwordsMatch = passwords.password === passwords.confirmPassword;
  const isFormValid = token && allRulesPass(passwords.password) && passwordsMatch;

  const toggleVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!isFormValid) return;

    setIsLoading(true);
    try {
      await apiRequest(buildApiUrl(API_ENDPOINTS.setPassword), {
        method: "POST",
        body: JSON.stringify({ token, password: passwords.password }),
      });
      setSuccess(true);
    } catch (err) {
      setError(err.message || "Error de conexión. Intenta nuevamente.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!initialized) return null;

  if (!token) {
    return (
      <div className="login-page">
        <div className="login-container">
          <h2>{APP_DISPLAY_NAME}</h2>
          <p
            style={{
              color: "var(--error-color)",
              textAlign: "center",
              fontSize: "11px",
              marginBottom: "20px",
            }}
          >
            El enlace no es válido o ha expirado.
          </p>
          <button type="button" className="login-submit-btn" onClick={() => navigate("/login")}>
            Ir a iniciar sesión
          </button>
          <p
            style={{
              textAlign: "center",
              marginTop: "12px",
              fontSize: "11px",
              color: "var(--text-secondary)",
            }}
          >
            ¿El enlace expiró?{" "}
            <button
              type="button"
              style={{
                background: "none",
                border: "none",
                color: "var(--accent-color)",
                cursor: "pointer",
                textDecoration: "underline",
                fontSize: "11px",
                padding: 0,
              }}
              onClick={() => navigate("/forgot-password")}
            >
              Solicita uno nuevo
            </button>
          </p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="login-page">
        <div className="login-container">
          <h2>{APP_DISPLAY_NAME}</h2>
          <p
            style={{
              color: "var(--accent-color)",
              textAlign: "center",
              fontSize: "11px",
              marginBottom: "20px",
            }}
          >
            Tu cuenta quedó activada. Ya puedes iniciar sesión.
          </p>
          <button type="button" className="login-submit-btn" onClick={() => navigate("/login")}>
            Ir a iniciar sesión
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <h2>Activar cuenta</h2>
        <p
          style={{
            color: "var(--text-secondary)",
            fontSize: "11px",
            marginBottom: "20px",
          }}
        >
          Establece tu contraseña para activar tu cuenta en {APP_DISPLAY_NAME}.
        </p>

        <form onSubmit={handleSubmit}>
          {error && (
            <div
              style={{
                color: "var(--error-color)",
                textAlign: "center",
                marginBottom: "15px",
                fontSize: "11px",
              }}
            >
              {error}
            </div>
          )}

          <div className="input-group">
            <label htmlFor="password">Nueva Contraseña</label>
            <div className="password-container">
              <input
                id="password"
                className="password-input"
                type={showPasswords.password ? "text" : "password"}
                value={passwords.password}
                placeholder="Ingresa tu contraseña"
                onChange={(e) =>
                  setPasswords({ ...passwords, password: e.target.value })
                }
                disabled={isLoading}
              />
              <span
                className="password-toggle-icon"
                onClick={() => toggleVisibility("password")}
              >
                <i
                  className={
                    showPasswords.password ? "fas fa-eye-slash" : "fas fa-eye"
                  }
                ></i>
              </span>
            </div>
            <PasswordStrengthChecklist password={passwords.password} />
          </div>

          <div className="input-group">
            <label htmlFor="confirmPassword">Confirmar Contraseña</label>
            <div className="password-container">
              <input
                id="confirmPassword"
                className="password-input"
                type={showPasswords.confirmPassword ? "text" : "password"}
                value={passwords.confirmPassword}
                placeholder="Confirma tu contraseña"
                onChange={(e) =>
                  setPasswords({ ...passwords, confirmPassword: e.target.value })
                }
                disabled={isLoading}
              />
              <span
                className="password-toggle-icon"
                onClick={() => toggleVisibility("confirmPassword")}
              >
                <i
                  className={
                    showPasswords.confirmPassword
                      ? "fas fa-eye-slash"
                      : "fas fa-eye"
                  }
                ></i>
              </span>
            </div>
            <p
              className={`error-message ${
                passwords.confirmPassword.length > 0 && !passwordsMatch
                  ? "visible"
                  : ""
              }`}
            >
              Las contraseñas no coinciden
            </p>
          </div>

          <button type="submit" disabled={isLoading || !isFormValid}>
            {isLoading ? "Activando..." : "Establecer contraseña"}
          </button>
        </form>

        {error && (
          <p
            style={{
              textAlign: "center",
              marginTop: "12px",
              fontSize: "11px",
              color: "var(--text-secondary)",
            }}
          >
            ¿El enlace expiró?{" "}
            <button
              type="button"
              style={{
                background: "none",
                border: "none",
                color: "var(--accent-color)",
                cursor: "pointer",
                textDecoration: "underline",
                fontSize: "11px",
                padding: 0,
              }}
              onClick={() => navigate("/forgot-password")}
            >
              Solicita uno nuevo
            </button>
          </p>
        )}

        <div className="links">
          <button
            type="button"
            style={{
              background: "none",
              border: "none",
              color: "var(--accent-color)",
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => navigate("/login")}
          >
            Volver al inicio de sesión
          </button>
        </div>
      </div>
    </div>
  );
};

export default SetPassword;
